import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import { comics } from "@/mocks/comics";

type SortKey = "score" | "views" | "chapters";

const sortMeta: { key: SortKey; label: string }[] = [
  { key: "score", label: "评分最高" },
  { key: "views", label: "人气最热" },
  { key: "chapters", label: "更新最多" },
];

export default function Rank() {
  const [sort, setSort] = useState<SortKey>("score");

  const list = useMemo(() => {
    const result = [...comics];
    if (sort === "score") result.sort((a, b) => b.score - a.score);
    if (sort === "views") result.sort((a, b) => Number(b.views.replace("万", "")) - Number(a.views.replace("万", "")));
    if (sort === "chapters") result.sort((a, b) => b.chapters - a.chapters);
    return result;
  }, [sort]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Seo
        title="卡通动漫第一页热门榜单 - 高分人气漫画排行榜"
        description="卡通动漫第一页热门榜单，依据评分、人气与更新热度动态排行的高分漫画与人气连载排行榜，帮你快速锁定当下最值得追的作品。"
        keywords="卡通动漫第一页热门榜单,漫画排行榜,高分漫画,人气连载榜"
      />
      <Navbar />

      <main className="bg-background-50 pt-16 md:pt-20">
        <div className="container-x">
          <nav className="flex items-center gap-2 py-6 text-sm text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-700 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line" style={{ fontSize: "16px" }}></i>
            <span className="text-foreground-800">热门榜单</span>
          </nav>

          <div className="flex flex-col gap-4 border-b border-background-200 pb-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="flex items-center gap-3 font-display text-3xl text-foreground-950">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500 text-background-50">
                  <i className="ri-fire-fill" style={{ fontSize: "22px" }}></i>
                </span>
                热门榜单
              </h1>
              <p className="mt-2 text-sm text-foreground-500">依据评分、人气与更新热度动态排行的高分漫画与人气连载排行榜</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {sortMeta.map((m) => (
                <button
                  key={m.key}
                  onClick={() => setSort(m.key)}
                  className={`rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                    sort === m.key ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 ring-1 ring-background-200 hover:bg-background-200"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 py-8">
            {list.map((c, i) => (
              <Link
                key={c.id}
                to={`/comic/${c.id}`}
                className="group flex flex-col overflow-hidden rounded-xl bg-background-100 ring-1 ring-background-200 transition-all duration-300 hover:-translate-y-0.5 hover:ring-primary-300 sm:flex-row"
                aria-label={`查看排行第${i + 1}名《${c.title}》`}
              >
                <div className="relative flex items-center gap-4 p-4 sm:w-64 sm:flex-shrink-0 sm:items-stretch sm:p-0">
                  <span
                    className={`flex h-10 w-10 items-center justify-center rounded-lg font-display text-xl text-background-50 ${
                      i < 3 ? "bg-accent-500" : "bg-foreground-300"
                    }`}
                  >
                    {i + 1}
                  </span>
                  <div className="relative aspect-[3/4] w-24 overflow-hidden rounded-lg sm:w-full sm:rounded-none">
                    <img src={c.cover} alt={`热门榜第${i + 1}名 ${c.title} 封面`} title={`${c.title} 排行榜`} className="h-full w-full object-cover object-top" loading="lazy" />
                  </div>
                </div>
                <div className="flex flex-1 flex-col p-4 sm:p-5">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-display text-xl text-foreground-950 group-hover:text-primary-700 transition-colors">{c.title}</h2>
                    <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs text-secondary-800">{c.category}</span>
                    <span className={`ml-auto flex items-center gap-1 text-base font-medium ${i < 3 ? "text-accent-600" : "text-primary-600"}`}>
                      <i className="ri-star-fill" style={{ fontSize: "15px" }}></i> {c.score}
                    </span>
                  </div>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{c.detail}</p>
                  <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
                    <span className="flex items-center gap-1.5"><i className="ri-eye-line" style={{ fontSize: "14px" }}></i> 人气 {c.views}</span>
                    <span className="flex items-center gap-1.5"><i className="ri-book-2-line" style={{ fontSize: "14px" }}></i> {c.chapters} 话</span>
                    <span className="flex items-center gap-1.5"><i className="ri-refresh-line" style={{ fontSize: "14px" }}></i> {c.updates}</span>
                    <span className="flex items-center gap-1.5"><i className="ri-user-line" style={{ fontSize: "14px" }}></i> {c.author}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>

      <Footer />
      <BackToTop />
    </>
  );
}